/**
 * @cafe-os/db — super-admin Settings RPCs (00105 + superAdminIntegrationsStatus EF).
 *
 * Всё gated by is_super_admin() в DB + JWT super_admin role в EF.
 */
import { callRpcWithDefaults } from "./errors";
import { getSupabaseClient } from "./client";

// ============================================================================
// 1. Plans editor
// ============================================================================

export interface PlanRowFull {
  id: string;
  name: Record<string, string>;
  monthly_price: number;
  currency: "KZT" | "USD" | "RUB" | "UZS" | "KGS" | "EUR";
  cycle_months: number;
  features: Record<string, unknown>;
  is_active: boolean;
  display_order: number;
  created_at: string;
  sub_count: number;
}

export interface UpsertPlanInput {
  id: string;
  name: Record<string, string>;
  monthly_price: number;
  currency: "KZT" | "USD" | "RUB" | "UZS" | "KGS" | "EUR";
  cycle_months: number;
  features: Record<string, unknown>;
  is_active: boolean;
  display_order: number;
}

export async function superAdminListPlans(): Promise<PlanRowFull[]> {
  const data = await callRpcWithDefaults<PlanRowFull[]>("super_admin_list_plans", {});
  return Array.isArray(data) ? data : [];
}

export async function superAdminUpsertPlan(input: UpsertPlanInput): Promise<{ ok: boolean; op: string; plan_id: string }> {
  const data = await callRpcWithDefaults<{ ok: boolean; op: string; plan_id: string }>(
    "super_admin_upsert_plan",
    {
      p_id:            input.id,
      p_name:          input.name,
      p_monthly_price: input.monthly_price,
      p_currency:      input.currency,
      p_cycle_months:  input.cycle_months,
      p_features:      input.features,
      p_is_active:     input.is_active,
      p_display_order: input.display_order,
    },
  );
  if (!data) throw new Error("[superAdminUpsertPlan] empty response");
  return data;
}

export async function superAdminDeletePlan(planId: string): Promise<void> {
  await callRpcWithDefaults("super_admin_delete_plan", { p_id: planId });
}

// ============================================================================
// 2. Domains overview
// ============================================================================

export interface DomainOverviewRow {
  tenant_id: string;
  tenant_name: Record<string, string> | null;
  is_active: boolean;
  computed_domain: string;
  custom_domain: string | null;
  vercel_status: string | null;
  created_at: string;
}

export async function superAdminDomainsOverview(): Promise<DomainOverviewRow[]> {
  const data = await callRpcWithDefaults<DomainOverviewRow[]>("super_admin_domains_overview", {});
  return Array.isArray(data) ? data : [];
}

export interface SetCustomDomainResult {
  ok: boolean;
  tenant_id: string;
  custom_domain: string | null;
  vercel_domain_status: "pending" | null;
}

/**
 * Назначить (domain) или снять (null) custom domain тенанта — 00448.
 * Сервер нормализует lower/trim, валидирует hostname (22023), отвергает
 * *.e-menu.bar (22023) и домены, занятые другим тенантом (23505).
 * Привязка домена в Vercel — отдельный ручной шаг (status остаётся pending).
 */
export async function superAdminSetTenantCustomDomain(
  tenantId: string,
  domain: string | null,
): Promise<SetCustomDomainResult> {
  const data = await callRpcWithDefaults<SetCustomDomainResult>(
    "super_admin_set_tenant_custom_domain",
    { p_tenant_id: tenantId, p_domain: domain },
  );
  if (!data?.ok) throw new Error("[db/superAdminSetTenantCustomDomain] empty response");
  return data;
}

// ============================================================================
// 3. Payments view (invoices + cron)
// ============================================================================

export interface RecentInvoiceRow {
  id: string;
  tenant_id: string;
  tenant_name: Record<string, string> | null;
  plan_id: string;
  amount: number;
  currency: string;
  status: "pending" | "paid" | "failed" | "refunded" | "void";
  period_start: string;
  period_end: string;
  due_at: string;
  paid_at: string | null;
  kaspi_link: string | null;
  created_at: string;
}

export async function superAdminRecentInvoices(limit = 50): Promise<RecentInvoiceRow[]> {
  const data = await callRpcWithDefaults<RecentInvoiceRow[]>(
    "super_admin_recent_invoices",
    { p_limit: limit },
  );
  return Array.isArray(data) ? data : [];
}

export interface CronJobRow {
  jobid: number;
  jobname: string;
  schedule: string;
  command: string;
  active: boolean;
  last_run: string | null;
  last_status: string | null;
}

export interface CronStatus {
  available: boolean;
  jobs: CronJobRow[];
  error?: string;
}

export async function superAdminCronStatus(): Promise<CronStatus> {
  const data = await callRpcWithDefaults<CronStatus>("super_admin_cron_status", {});
  if (!data) return { available: false, jobs: [] };
  return data;
}

// ============================================================================
// 5. Security: allowlist + audit log
// ============================================================================

export interface AllowlistEntry {
  email: string;
  added_at: string;
  added_by: string | null;
  is_current: boolean;
}

export async function superAdminListAllowlist(): Promise<AllowlistEntry[]> {
  const data = await callRpcWithDefaults<AllowlistEntry[]>("super_admin_list_allowlist", {});
  return Array.isArray(data) ? data : [];
}

export async function superAdminAddToAllowlist(email: string, note?: string): Promise<void> {
  await callRpcWithDefaults("super_admin_add_to_allowlist", {
    p_email: email,
    p_note: note ?? null,
  });
}

export async function superAdminRemoveFromAllowlist(email: string): Promise<void> {
  await callRpcWithDefaults("super_admin_remove_from_allowlist", { p_email: email });
}

export interface AuditEntry {
  id: number;
  actor_email: string;
  action: string;
  target_kind: string | null;
  target_id: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}

export async function superAdminAuditRecent(limit = 100, actionFilter?: string): Promise<AuditEntry[]> {
  const data = await callRpcWithDefaults<AuditEntry[]>("super_admin_audit_recent", {
    p_limit: limit,
    p_action_filter: actionFilter ?? null,
  });
  return Array.isArray(data) ? data : [];
}

// ============================================================================
// 6. Integrations: outbox status + secrets check (via Edge Function)
// ============================================================================

export interface OutboxTypeBreakdown {
  type: string;
  pending: number;
  sent: number;
  failed: number;
  dead: number;
  total: number;
}

export interface OutboxStatus {
  last_24h: OutboxTypeBreakdown[];
  totals: {
    pending: number;
    sent_24h: number;
    failed_24h: number;
    dead_total: number;
  };
  oldest_pending: string | null;
}

export async function superAdminOutboxStatus(): Promise<OutboxStatus> {
  const data = await callRpcWithDefaults<OutboxStatus>("super_admin_outbox_status", {});
  if (!data) {
    return { last_24h: [], totals: { pending: 0, sent_24h: 0, failed_24h: 0, dead_total: 0 }, oldest_pending: null };
  }
  return data;
}

export interface IntegrationCheck {
  key: string;
  label: string;
  category: "ai" | "telegram" | "push" | "payment" | "deploy";
  configured: boolean;
  masked?: string;
}

export interface IntegrationsStatusResult {
  ok: boolean;
  summary: { total: number; configured: number; missing: string[] };
  integrations: IntegrationCheck[];
  actor_email: string;
  checked_at: string;
}

export async function superAdminIntegrationsStatus(): Promise<IntegrationsStatusResult> {
  const sb = getSupabaseClient();
  const { data, error } = await sb.functions.invoke<IntegrationsStatusResult>(
    "superAdminIntegrationsStatus",
    { body: {} },
  );
  if (error) throw error;
  if (!data) throw new Error("[superAdminIntegrationsStatus] empty response");
  return data;
}

// ============================================================================
// 7. Subscription alerts (00106) — manual cron trigger
// ============================================================================

export interface SubscriptionAlertsResult {
  processed: number;
  skipped_dedup: number;
  alerts: Array<{
    subscription_id: string;
    tenant_id: string;
    alert_type: "trial_3d" | "trial_1d" | "past_due_3d" | "past_due_7d" | "blocked";
  }>;
  ran_at: string;
}

/**
 * Запустить subscription_alerts cron вручную из super-admin UI
 * (полезно для проверки что Telegram-уведомления приходят).
 * Cron в pg_cron сам запускается каждый день в 03:00 UTC.
 */
export async function superAdminRunSubscriptionAlertsNow(): Promise<SubscriptionAlertsResult> {
  const data = await callRpcWithDefaults<SubscriptionAlertsResult>(
    "super_admin_run_subscription_alerts_now",
    {},
  );
  if (!data) throw new Error("[superAdminRunSubscriptionAlertsNow] empty");
  return data;
}
